import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

// Styled components for custom styling
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: "10px",
  borderRadius: "8px",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(90deg, #00BB9F 0%, #6CD8BF 100%)",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  color: "#FFFFFF",
  width: (props) => props.width || "auto", // Use width prop or default to auto
}));

const BodyTableCell = styled(TableCell)(({ theme, idx }) => ({
  backgroundColor: idx % 2 === 0 ? theme.palette.common.white : "#F8F7FD",
  paddingLeft: "16px",
  paddingRight: "16px",
  width: (props) => props.width || "auto", // Use width prop or default to auto
}));

const TableModal = ({ columns, data, hiddenColumns = [] }) => {
  // console.log("check data on table Modal", data);

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    const dataArray = Array.isArray(data) ? data : [data];
    if (!sortColumn) return dataArray;

    return dataArray.sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      } else {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue, ["th", "en"], { sensitivity: "base" })
          : bValue.localeCompare(aValue, ["th", "en"], { sensitivity: "base" });
      }
    });
  }, [data, sortColumn, sortDirection]);

  const visibleColumns = columns.filter(
    (column) => !hiddenColumns.includes(column.accessor)
  );

  const parseCustomDateString = (dateString) => {
    const dateTimeRegex =
      /^(\d{2})\/(\d{2})\/(\d{4}) เวลา (\d{2}):(\d{2}):(\d{2}) น\.$/;
    const match = dateString.match(dateTimeRegex);

    if (!match) {
      return { formattedDate: "Invalid date", formattedTime: "" };
    }

    const [, day, month, year, hours, minutes, seconds] = match;

    const dateObj = new Date(
      `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
    );

    if (isNaN(dateObj.getTime())) {
      return { formattedDate: "Invalid date", formattedTime: "" };
    }

    const dateOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };

    const formattedDate = dateObj.toLocaleDateString("th-TH", dateOptions);
    const formattedTime = dateObj.toLocaleTimeString("th-TH", timeOptions);

    return { formattedDate, formattedTime };
  };

  if (!Array.isArray(data)) {
    console.error("Data is not an array:", data);
    return null;
  }

  return (
    <StyledTableContainer component={Paper}>
      <Table>
        <StyledTableHead>
          <TableRow>
            {visibleColumns.map((column) => (
              <StyledTableCell key={column.accessor} width={column.width}>
                {column.Header}
              </StyledTableCell>
            ))}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {sortedData?.length > 0 ? (
            sortedData
              .filter((row) => !row.parentId)
              .map((row, rowIndex) => (
                <TableRow key={row.id}>
                  {visibleColumns.map((column, colIndex) => (
                    <BodyTableCell
                      key={colIndex}
                      idx={rowIndex}
                      width={column.width}
                    >
                      {column.accessor === "index" ? (
                        rowIndex + 1
                      ) : column.accessor === "created_date" ? (
                        <div>
                          {
                            parseCustomDateString(row.created_date)
                              .formattedDate
                          }
                          <div>
                            เวลา{" "}
                            {
                              parseCustomDateString(row.created_date)
                                .formattedTime
                            }{" "}
                            น.
                          </div>
                        </div>
                      ) : column.accessor === "due_date" ? (
                        <div>
                          {parseCustomDateString(row.due_date).formattedDate}
                          <div>
                            เวลา{" "}
                            {parseCustomDateString(row.due_date).formattedTime}{" "}
                            น.
                          </div>
                        </div>
                      ) : column.accessor === "actions" ? (
                        column.Cell({ row })
                      ) : column.accessor === "picture" ? (
                        row.picture && row.picture.length > 0 ? (
                          <img
                            src={row.picture[0].picture}
                            alt="รูปภาพ"
                            style={{
                              width: "150px",
                              height: "150px",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                        ) : (
                          <Box
                            style={{
                              width: "150px",
                              height: "150px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#F8F7FD",
                              color: "#B0B0B0",
                            }}
                          >
                            ไม่มีรูปภาพ
                          </Box>
                        )
                      ) : (
                        row[column.accessor]
                      )}
                    </BodyTableCell>
                  ))}
                </TableRow>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={visibleColumns.length} align="center">
                ไม่พบข้อมูล
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default TableModal;
